<template>
  <transition name="fade">
    <div v-if="visible" class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black bg-opacity-50 p-4 sm:p-6">
      <div class="relative w-full max-w-lg rounded-lg bg-white shadow-xl ring-1 ring-gray-200">
        
        <!-- Header -->
        <div class="flex items-center justify-between border-b px-4 py-3 bg-gray-50 rounded-t-lg">
          <h3 class="text-lg font-medium text-gray-900">
            Informations du client
          </h3>
          <button @click="close" class="text-gray-400 hover:text-gray-500 focus:outline-none">
            <span class="sr-only">Fermer</span>
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6 space-y-4">
          <!-- Champ de recherche email -->
          <div v-if="!clientInfo">
            <label class="block text-sm font-medium text-gray-700 mb-2">Email du client</label>
            <div class="flex gap-2">
              <input
                type="email"
                v-model="emailSearch"
                placeholder="client@example.com"
                @keyup.enter="fetchClientInfo"
                class="flex-1 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm p-2 border"
              />
              <button
                @click="fetchClientInfo"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                :disabled="loading || !emailSearch"
              >
                <span v-if="loading" class="mr-2">⏳</span>
                {{ loading ? 'Recherche...' : 'Rechercher' }}
              </button>
            </div>
            <p class="mt-2 text-xs text-gray-500">Utilisez l'email du client pour vérifier ses informations</p>
          </div>

          <!-- État de chargement -->
          <div v-if="loading" class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p class="mt-2 text-sm text-gray-600">Recherche des informations...</p>
          </div>

          <!-- Erreur -->
          <div v-else-if="error" class="text-center py-4">
            <div class="text-red-600 text-sm mb-2">{{ error }}</div>
            <button
              @click="resetSearch"
              class="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Nouvelle recherche
            </button>
          </div>

          <!-- Informations client -->
          <div v-else-if="clientInfo" class="space-y-4">
            <div class="bg-green-50 border border-green-200 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-3">
                <svg class="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 class="text-sm font-semibold text-green-900">Client trouvé</h4>
              </div>
              
              <div class="space-y-3 text-sm">
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <span class="text-gray-600 font-medium">Email :</span>
                    <p class="text-gray-900 mt-1">{{ clientInfo.email }}</p>
                  </div>
                  <div v-if="clientInfo.nom">
                    <span class="text-gray-600 font-medium">Nom :</span>
                    <p class="text-gray-900 mt-1">{{ clientInfo.nom }}</p>
                  </div>
                  <div v-if="clientInfo.prenom">
                    <span class="text-gray-600 font-medium">Prénom :</span>
                    <p class="text-gray-900 mt-1">{{ clientInfo.prenom }}</p>
                  </div>
                  <div v-if="clientInfo.langue">
                    <span class="text-gray-600 font-medium">Langue :</span>
                    <p class="text-gray-900 mt-1">{{ clientInfo.langue.toUpperCase() }}</p>
                  </div>
                </div>

                <!-- Informations supplémentaires si disponibles -->
                <div v-if="clientInfo.comptes" class="pt-3 border-t border-green-200">
                  <span class="text-gray-600 font-medium">Comptes actifs :</span>
                  <p class="text-gray-900 mt-1">{{ clientInfo.comptes }}</p>
                </div>
              </div>
            </div>

            <button
              @click="resetSearch"
              class="w-full text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Rechercher un autre client
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 border-t bg-gray-50 px-4 py-3 rounded-b-lg">
          <button
            type="button"
            class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            @click="close"
            :disabled="loading"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from '../../plugins/axios';

export default {
  name: 'ClientInfoModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    client: {
      type: Object,
      default: null
    }
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      error: null,
      clientInfo: null,
      emailSearch: ''
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Si un client est passé en prop, utiliser son email
        if (this.client && this.client.Email_Gologin) {
          this.emailSearch = this.client.Email_Gologin;
          this.fetchClientInfo();
        } else {
          this.resetSearch();
        }
      } else {
        this.resetSearch();
      }
    }
  },
  methods: {
    close() {
      if (!this.loading) {
        this.$emit('close');
      }
    },
    resetSearch() {
      this.loading = false;
      this.error = null;
      this.clientInfo = null;
      this.emailSearch = this.client?.Email_Gologin || '';
    },
    async fetchClientInfo() {
      if (!this.emailSearch) {
        this.error = 'Veuillez saisir un email';
        return;
      }

      this.loading = true;
      this.error = null;
      this.clientInfo = null;

      try {
        const response = await axios.get('/admin2/client-info', {
          params: {
            email: this.emailSearch
          }
        });

        if (response.data) {
          this.clientInfo = response.data;
        } else {
          this.error = 'Aucune information trouvée pour cet email.';
        }
      } catch (err) {
        console.error('Erreur récupération infos client :', err);
        if (err.response?.status === 404) {
          this.error = 'Client non trouvé pour cet email.';
        } else {
          this.error = err.response?.data?.error || err.message || 'Erreur lors de la récupération des informations.';
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

